import { useState, type ReactNode } from 'react';
import type { PersonItem } from '../../shared/person/person_row.js';
import {
  PickerHeader,
  TravelerPicker,
  TravelerPickerField,
  travelerSummary,
  travelerTrigger,
  type TravelerPickerCopy,
} from '../../shared/person/traveler_picker.js';
import {
  TravelerPickerFooter,
  TravelerPolicyRow,
} from '../../shared/person/traveler_picker_footer.js';
import type { TravelerFieldSearch } from '../../shared/person/who_search.js';
import { resolvePolicyTraveler } from '../../shared/person/traveler_policy.js';
import { useGuidedOpen, type FieldGuide } from '../../shared/guided_flow.js';
import { BUS_MAX_TRAVELERS, toggleBusTraveler } from './bus_search_rules.js';

export type BusTravelersCopy = TravelerPickerCopy & {
  /** "2 viajantes" — o gatilho com mais de uma pessoa. */
  countLabel(count: number): string;
  backLabel: string;
  /** "Aplicar política de" — a linha do pé com 2+ viajantes e o título da lista. */
  policyLabel: string;
  /** "2 viajantes na busca" — o resumo do pé, anunciado a cada mudança. */
  inSearchLabel(count: number): string;
  /** O fechar do pé ("Pronto"), o mesmo do aéreo e do hotel. */
  doneLabel: string;
};

/** A lista da política: os viajantes da busca em modo único. */
function BusPolicyList({
  travelers,
  subject,
  copy,
  locale,
  onBack,
  onPick,
}: {
  readonly travelers: readonly PersonItem[];
  readonly subject: PersonItem;
  readonly copy: BusTravelersCopy;
  readonly locale: string | undefined;
  readonly onBack: () => void;
  readonly onPick: (id: string) => void;
}): ReactNode {
  return (
    <TravelerPicker
      mode="single"
      items={travelers}
      selected={[subject]}
      searchable={false}
      query=""
      searchState="ready"
      hasMore={false}
      copy={copy}
      {...(locale ? { locale } : {})}
      header={<PickerHeader title={copy.policyLabel} backLabel={copy.backLabel} onBack={onBack} />}
      onQueryChange={() => {}}
      onSelect={(person) => {
        onPick(person.id);
      }}
    />
  );
}

/** A escolha do QUEM: quem viaja e de quem é a política (sempre um deles). */
export type BusTravelersChange = {
  readonly travelers: readonly PersonItem[];
  /** Ausente só com a lista vazia — o picker nunca desce de 1. */
  readonly policyTravelerId?: string;
};

/**
 * O QUEM do ônibus: o seletor de pessoas compartilhado (aéreo, hotel, carro)
 * em modo multi — cada viajante escolhe uma poltrona por trecho na reserva.
 * Mínimo 1, teto `BUS_MAX_TRAVELERS`. O pé é o do aéreo e do hotel: a contagem
 * anunciada e o «Pronto»; com 2+ viajantes, também de quem é a POLÍTICA, que
 * abre a mesma lista em modo único — a régua do aéreo e do hotel
 * (`resolvePolicyTraveler`): o solicitante pode reservar só para terceiros, e
 * aí a política é de quem viaja.
 */
export function BusTravelersField({
  travelers,
  policyTravelerId,
  search,
  copy,
  disabled,
  locale,
  guide,
  onChange,
}: {
  readonly travelers: readonly PersonItem[];
  readonly policyTravelerId?: string;
  readonly search: TravelerFieldSearch;
  readonly copy: BusTravelersCopy;
  readonly disabled: boolean;
  readonly locale?: string;
  /** O guia da barra: o Buscar travado pelo QUEM vazio pede a abertura. */
  readonly guide?: FieldGuide;
  readonly onChange: (next: BusTravelersChange) => void;
}): ReactNode {
  const [open, setOpen] = useGuidedOpen(guide);
  const [view, setView] = useState<'list' | 'policy'>('list');
  const requesterId = search.pinned?.id;
  const summary = travelerSummary(
    travelers,
    requesterId,
    { you: copy.you, count: (n) => copy.countLabel(n) },
    locale,
  );
  const subjectId = resolvePolicyTraveler(travelers, policyTravelerId, requesterId);
  const subject =
    travelers.length > 1 ? travelers.find((person) => person.id === subjectId) : undefined;
  const close = () => {
    setOpen(false);
    setView('list');
  };
  return (
    <TravelerPickerField
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else close();
      }}
      title={view === 'policy' ? copy.policyLabel : copy.label}
      backLabel={copy.backLabel}
      trigger={travelerTrigger({
        people: travelers,
        summary,
        label: copy.label,
        expanded: open,
        disabled,
        ...(locale ? { locale } : {}),
        onOpen: () => {
          if (open) close();
          else setOpen(true);
        },
      })}
      {...(view === 'list'
        ? {
            footer: (
              <TravelerPickerFooter
                {...(subject
                  ? {
                      policy: (
                        <TravelerPolicyRow
                          subject={subject}
                          requesterId={requesterId}
                          label={copy.policyLabel}
                          youLabel={copy.you}
                          locale={locale}
                          onChoose={() => {
                            setView('policy');
                          }}
                        />
                      ),
                    }
                  : {})}
                summary={copy.inSearchLabel(travelers.length)}
                doneLabel={copy.doneLabel}
                onDone={close}
              />
            ),
          }
        : {})}
    >
      {subject && view === 'policy' ? (
        <BusPolicyList
          travelers={travelers}
          subject={subject}
          copy={copy}
          locale={locale}
          onBack={() => {
            setView('list');
          }}
          onPick={(id) => {
            onChange({ travelers, policyTravelerId: id });
            setView('list');
          }}
        />
      ) : (
        <TravelerPicker
          mode="multi"
          items={search.items}
          selected={travelers}
          {...(search.pinned ? { pinned: search.pinned } : {})}
          min={1}
          max={BUS_MAX_TRAVELERS}
          searchable={search.searchable}
          query={search.query}
          searchState={search.state}
          hasMore={search.hasMore}
          copy={copy}
          {...(locale ? { locale } : {})}
          onQueryChange={search.onQueryChange}
          {...(search.onLoadMore ? { onLoadMore: search.onLoadMore } : {})}
          onSelect={(person) => {
            const next = toggleBusTraveler(travelers, person);
            const policy = resolvePolicyTraveler(next, policyTravelerId, requesterId);
            onChange({ travelers: next, ...(policy ? { policyTravelerId: policy } : {}) });
          }}
        />
      )}
    </TravelerPickerField>
  );
}
